/**
 * MultipleChoiceQuestion Component
 * Checkbox question type - multiple answers can be selected
 */

import React from 'react';
import { CheckCircle, XCircle, Check } from 'lucide-react';
import { QuestionLearner } from '@/types/assessment.types';

export interface MultipleChoiceQuestionProps {
  question: QuestionLearner;
  selectedAnswers: string[]; // Array of option IDs
  onSelect: (optionIds: string[]) => void;
  disabled?: boolean;
  showCorrect?: boolean;
  correctAnswerIds?: string[]; // For results view
}

export const MultipleChoiceQuestion: React.FC<MultipleChoiceQuestionProps> = ({
  question,
  selectedAnswers = [],
  onSelect,
  disabled = false,
  showCorrect = false,
  correctAnswerIds = [],
}) => {
  const handleToggle = (optionId: string) => {
    if (disabled) return;

    if (selectedAnswers.includes(optionId)) {
      onSelect(selectedAnswers.filter(id => id !== optionId));
    } else {
      onSelect([...selectedAnswers, optionId]);
    }
  };

  const getOptionStyle = (optionId: string) => {
    const isSelected = selectedAnswers.includes(optionId);
    const isCorrect = showCorrect && correctAnswerIds.includes(optionId);
    const shouldBeSelected = showCorrect && correctAnswerIds.includes(optionId);
    const isIncorrect = showCorrect && isSelected && !correctAnswerIds.includes(optionId);

    if (isIncorrect) {
      return 'border-red-300 bg-red-50';
    }
    if (isCorrect) {
      return 'border-green-300 bg-green-50';
    }
    if (shouldBeSelected && !isSelected) {
      return 'border-green-200 bg-green-50';
    }
    if (isSelected) {
      return 'border-primary-500 bg-primary-50';
    }
    return 'border-gray-200 hover:border-gray-300 hover:bg-gray-50';
  };

  return (
    <div className="space-y-3">
      {/* Question Text */}
      <div className="flex items-start gap-2 mb-4">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">{question.question_text}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            <span className="px-2 py-0.5 bg-gray-100 rounded">{question.points} points</span>
            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded">
              Select all that apply
            </span>
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-2">
        {question.options?.map(option => {
          const isSelected = selectedAnswers.includes(option.id);
          const isCorrect = showCorrect && correctAnswerIds.includes(option.id);
          const isIncorrect = showCorrect && isSelected && !correctAnswerIds.includes(option.id);

          return (
            <button
              key={option.id}
              onClick={() => handleToggle(option.id)}
              disabled={disabled}
              className={`w-full text-left p-3 border rounded-lg transition-all ${getOptionStyle(
                option.id
              )} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-center gap-3">
                {/* Checkbox */}
                <div
                  className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isSelected
                      ? isIncorrect
                        ? 'border-red-500 bg-red-500'
                        : 'border-primary-500 bg-primary-500'
                      : 'border-gray-300'
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>

                {/* Option Text */}
                <span className="flex-1 text-sm text-gray-900">{option.text}</span>

                {/* Correct/Incorrect Indicator */}
                {showCorrect && isCorrect && (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                )}
                {showCorrect && isIncorrect && (
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
